import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isTextEntry as isEditable } from '../utils/textEntry';
import { isAppActive, subscribeAppActivity } from '../utils/appActivity';
const RESTORE_EVENT = 'wiw:restore-viewport';

export function restoreViewportAfterEditing() {
  // Removing a focused contenteditable does not reliably emit focusout in
  // WebKit. Notify the persistent shell as well as the next screen.
  window.dispatchEvent(new Event(RESTORE_EVENT));
}

export function readViewport(layoutHeight = window.innerHeight, previous) {
  const viewport = window.visualViewport;
  // Pinch zoom should magnify the page, not rebuild its layout at a smaller size.
  const zoomed = viewport && Math.abs(viewport.scale - 1) > 0.01;
  if (zoomed && previous) return previous;
  const measuredHeight = Math.max(0, !zoomed && viewport ? viewport.height : window.innerHeight);
  const keyboardOpen = !zoomed && Boolean(isEditable(document.activeElement) || previous?.keyboardOpen)
    && Math.max(window.innerHeight, layoutHeight) - measuredHeight > 120;
  // iOS can retain both a reduced visual height and a pan after dismissal.
  // Use those measurements only while editing/closing the keyboard; a feed
  // with no keyboard must fill the layout viewport, with no synthetic inset.
  const keyboardFrame = !zoomed && viewport && (keyboardOpen || isEditable(document.activeElement));
  const height = keyboardFrame ? measuredHeight : window.innerHeight;
  const offsetTop = keyboardFrame ? Math.max(0, viewport.offsetTop) : 0;
  return {
    height,
    layoutHeight: Math.max(layoutHeight, window.innerHeight, height),
    offsetTop,
    bottom: Math.max(0, window.innerHeight - height - offsetTop),
    // Focus may move through body or a toolbar before the keyboard closes.
    // Keep the keyboard layout until the measured viewport actually recovers.
    keyboardOpen,
  };
}

export default function useViewport() {
  const [viewport, setViewport] = useState(readViewport);
  const lastViewport = useRef(viewport);
  const layout = useRef({ height: window.innerHeight, width: window.innerWidth });
  useLayoutEffect(() => {
    let restoreTimer;
    const update = () => {
      // A suspended native webview can briefly report an empty viewport.
      if (!isAppActive() || window.innerHeight <= 0 || window.innerWidth <= 0) return;
      const zoomed = window.visualViewport && Math.abs(window.visualViewport.scale - 1) > 0.01;
      if (!zoomed) {
        if (Math.abs(window.innerWidth - layout.current.width) > 80) {
          // After rotation, the old width approximates the unobstructed height.
          layout.current.height = lastViewport.current.keyboardOpen
            ? Math.max(window.innerHeight, layout.current.width) : window.innerHeight;
        } else if (!isEditable(document.activeElement) && !lastViewport.current.keyboardOpen) {
          layout.current.height = window.innerHeight;
        } else {
          layout.current.height = Math.max(layout.current.height, window.innerHeight);
        }
        layout.current.width = window.innerWidth;
      }
      const next = readViewport(layout.current.height, lastViewport.current);
      lastViewport.current = next;
      setViewport((previous) => Object.keys(next).every((key) => next[key] === previous[key]) ? previous : next);
    };
    const restore = (event) => {
      clearTimeout(restoreTimer);
      // Leave time for the closing animation and focus moving to another
      // field. A late/stale resize must not keep keyboardOpen true forever.
      restoreTimer = setTimeout(() => {
        if (isEditable(document.activeElement)) return;
        if (event?.type === RESTORE_EVENT && Math.abs((window.visualViewport?.scale ?? 1) - 1) <= 0.01
          && (window.scrollX || window.scrollY || window.visualViewport?.offsetTop)) {
          // WebKit can keep its document pan even with scrollY === 0. Nudge
          // the root after dismissal, never the comment's internal scroller.
          window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
        }
        lastViewport.current = { ...lastViewport.current, keyboardOpen: false };
        update();
      }, 350);
      update();
    };
    const focus = () => { clearTimeout(restoreTimer); update(); };
    const visualViewport = window.visualViewport;
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    document.addEventListener('focusin', focus);
    document.addEventListener('focusout', restore);
    window.addEventListener(RESTORE_EVENT, restore);
    window.addEventListener('pageshow', restore);
    const unsubscribe = subscribeAppActivity(event => {
      if (event.active && event.reason === 'resume') restore();
    });
    return () => {
      unsubscribe();
      clearTimeout(restoreTimer);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('focusout', restore);
      window.removeEventListener(RESTORE_EVENT, restore);
      window.removeEventListener('pageshow', restore);
    };
  }, []);
  return viewport;
}

export function useViewportCss() {
  const viewport = useViewport();
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-viewport-height', `${viewport.height}px`);
    root.style.setProperty('--app-layout-height', `${viewport.layoutHeight}px`);
    root.style.setProperty('--app-viewport-top', `${viewport.offsetTop}px`);
    root.style.setProperty('--app-viewport-bottom', `${viewport.bottom}px`);
    root.dataset.keyboardOpen = String(viewport.keyboardOpen);
  }, [viewport]);
  useEffect(() => () => {
    const root = document.documentElement;
    ['--app-viewport-height', '--app-layout-height', '--app-viewport-top', '--app-viewport-bottom'].forEach((name) => root.style.removeProperty(name));
    delete root.dataset.keyboardOpen;
  }, []);
}
